import { useCompanyDataServiceV1 } from "@rw1/api-services";
import { useQuery } from "@tanstack/react-query";

export const COMPANY_SETTINGS_QUERY_KEY = "company-settings";

export const SEAT_RENEWAL_SETTING_KEY = "seatRenewalEnabled";
export const ENABLE_ANALYTICS_SETTING_KEY = "EnableAnalytics";
export const WHITELIST_ENABLED_SETTING_KEY = "EnableLicenseWhitelist";
export const USER_LEVEL_DATA_CONSENT_SETTING_KEY = "EnableUserLevelDataConsent";
export const EXTERNAL_USER_LEVEL_DATA_CONSENT_SETTING_KEY =
  "EnableExternalUserLevelDataConsent";

interface UseCompanySettingsOptions {
  companyId?: string;
  enabled?: boolean;
}

interface UseCompanySettingsResult {
  settings: Record<string, string>;
  isLoading: boolean;
  isFetched: boolean;
  isSeatRenewalEnabled: boolean;
  isDataAnalyticsEnabled: boolean;
  isWhitelistEnabled: boolean;
  isUserLevelDataConsentEnabled: boolean;
  isExternalUserLevelDataConsentEnabled: boolean;
}

const isTruthyString = (value: string | undefined): boolean =>
  value?.toLowerCase() === "true";

export const useCompanySettings = ({
  companyId,
  enabled = true,
}: UseCompanySettingsOptions): UseCompanySettingsResult => {
  const companyDataService = useCompanyDataServiceV1();

  const { data, isLoading, isFetched } = useQuery({
    queryKey: [COMPANY_SETTINGS_QUERY_KEY, companyId],
    queryFn: async () => {
      if (!companyId) return [];

      return await companyDataService.getCompanySettings(companyId);
    },
    enabled: enabled && !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const settings: Record<string, string> = {};
  if (data) {
    for (const setting of data) {
      settings[setting.settingKey] = setting.settingValue;
    }
  }

  const isUserLevelDataConsentEnabled = isTruthyString(
    settings[USER_LEVEL_DATA_CONSENT_SETTING_KEY],
  );

  const isSeatRenewalEnabled = isTruthyString(
    settings[SEAT_RENEWAL_SETTING_KEY],
  );

  const isDataAnalyticsEnabled = isTruthyString(
    settings[ENABLE_ANALYTICS_SETTING_KEY],
  );

  const isWhitelistEnabled = isTruthyString(
    settings[WHITELIST_ENABLED_SETTING_KEY],
  );

  const isExternalUserLevelDataConsentEnabled = isTruthyString(
    settings[EXTERNAL_USER_LEVEL_DATA_CONSENT_SETTING_KEY],
  );

  return {
    settings,
    isLoading,
    isSeatRenewalEnabled,
    isDataAnalyticsEnabled,
    isWhitelistEnabled,
    isUserLevelDataConsentEnabled,
    isExternalUserLevelDataConsentEnabled,
    isFetched,
  };
};
